import type { TokenStorage } from "../types";
import { debugLog } from "./debug";

const ACCESS_TOKEN_KEY = `${import.meta.env.VITE_COOKIE_PREFIX || ''}access_token`


class TokenManager implements TokenStorage {
  private readonly ACCESS_TOKEN_KEY = ACCESS_TOKEN_KEY;

  constructor() {
  }


  getAccessToken(): string | null {
    return this.getCookie(this.ACCESS_TOKEN_KEY);
  }


  setAccessToken(token: string): void {
    debugLog("Setting access token");
    // Store in cookie for cross-tab sharing
    // This won't create XSS issues, as the cookie cannot be directly used to authenticate
    // Note: A possible improvement would be to set this as httpOnly and secure
    // (but then it can't be accessed by client-side code, and we need to know when
    // then token is about to expire for automatic refresh). Another solution (better)
    // would be to add a broadcast channel to notify other tabs of token changes.
    this.setCookie(this.ACCESS_TOKEN_KEY, token, {
      httpOnly: false, // Client-side cookie (needed for cross-tab access)
      secure: (import.meta?.env?.PROD ?? true) as boolean,
      sameSite: "strict",
      // 30 days, but the access token is usually short-lived and will expire sooner.
      // This is necessary to avoid issues with the cookie being deleted.
      // When this cookie exists, it means the user may be logged in. This will
      // automatically try to refresh the access token when the page loads.
      // In short: this is not the lifetime of the access token, but the lifetime of the cookie.
      maxAge: 60 * 60 * 24 * 30,
      path: "/",
      domain:  import.meta?.env?.PROD ? ".baback.co" : undefined,
    });
  }


  clearTokens(): void {
    debugLog("Clearing all tokens");
    // Clear cookies
    this.deleteCookie(this.ACCESS_TOKEN_KEY);
  }


  // Helper methods for cookie management
  private getCookie(name: string): string | null {
    if (typeof document === "undefined") return null;

    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(";").shift() || null;
    }
    return null;
  }


  private setCookie(name: string, value: string, options: {
    httpOnly?: boolean;
    secure?: boolean;
    sameSite?: "strict" | "lax" | "none";
    maxAge?: number;
    path?: string;
    domain?: string;
  } = {}): void {
    if (typeof document === "undefined") return;

    const {
      secure = false,
      sameSite = "strict",
      maxAge,
      path = "/",
      domain,
    } = options;

    let cookieString = `${name}=${value}; path=${path}`;

    if (secure) {
      cookieString += "; secure";
    }

    if (sameSite) {
      cookieString += `; samesite=${sameSite}`;
    }

    if (maxAge) {
      cookieString += `; max-age=${maxAge}`;
    }

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    document.cookie = cookieString;
  }


  private deleteCookie(name: string): void {
    if (typeof document === "undefined") return;

    const domain = import.meta?.env?.DEV ? undefined : ".baback.co";
    let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

    if (domain) {
      cookieString += ` domain=${domain};`;
    }

    document.cookie = cookieString;
  }


  // Get token expiration time
  getTokenExpiration(): number | null {
    const token = this.getAccessToken();
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp * 1000; // Convert to milliseconds
    } catch {
      return null;
    }
  }
}



export const tokenManager = new TokenManager();
